/**
 * Summary card visual mode for DoT Results.
 *
 * true  → SPARK Compliance Dashboard palette (#55718B → #B4C3D1 + chart accents)
 * false → previous mock pastel themes (bright blue / cream / lavender)
 *
 * Flip this single flag to revert card colors/gradients.
 */
export const USE_SPARK_SUMMARY_THEME = true;

export type SourceThemeKey = "spark" | "manual" | "third";

export type SourceCardTheme = {
  fg: string;
  labelFg: string;
  bg: string;
  border: string;
  iconBg: string;
  wave: string;
};

export type AttentionCardTheme = {
  fg: string;
  bg: string;
  border: string;
  iconBg: string;
};

export type SummaryAccentTheme = {
  scoreRing: string;
  scoreTrack: string;
  scoreShadow: string;
  divider: string;
  accentBlue: string;
  accentOrange: string;
  accentIndigo: string;
  selectedBorder: string;
  selectedShadow: string;
  selectedSectionBg: string;
  viewDetails: string;
  compliant: string;
  compliantChip: string;
  nonCompliant: string;
  nonCompliantChip: string;
  notSet: string;
  notSetChip: string;
  na: string;
  naChip: string;
  source: Record<SourceThemeKey, SourceCardTheme>;
  attention: {
    gaps: AttentionCardTheme;
    stale: AttentionCardTheme;
    unset: AttentionCardTheme;
    overridden: AttentionCardTheme;
  };
};

/** Mock pastel pack — previous summary look */
function pastelThemes(isDark: boolean): SummaryAccentTheme {
  return {
    scoreRing: "#2563EB",
    scoreTrack: isDark ? "rgba(255,255,255,0.08)" : "#DCE7F8",
    scoreShadow: isDark ? "none" : "drop-shadow(0 8px 16px rgba(37,99,235,0.18))",
    divider: isDark
      ? "linear-gradient(180deg, transparent, rgba(255,255,255,0.2), transparent)"
      : "linear-gradient(180deg, transparent, #B9CDEB, transparent)",
    accentBlue: "#3B82F6",
    accentOrange: "#F59E0B",
    accentIndigo: "#6366F1",
    selectedBorder: isDark ? "rgba(59,130,246,0.65)" : "#3B82F6",
    selectedShadow: isDark
      ? "0 0 0 2px rgba(59,130,246,0.25)"
      : "0 0 0 2px rgba(59,130,246,0.18), 0 8px 18px rgba(37,99,235,0.12)",
    selectedSectionBg: isDark ? "rgba(37,99,235,0.2)" : "#EFF6FF",
    viewDetails: "#2563EB",
    compliant: "#16A34A",
    compliantChip: isDark ? "rgba(22,163,74,0.18)" : "rgba(22,163,74,0.1)",
    nonCompliant: "#DC2626",
    nonCompliantChip: isDark ? "rgba(220,38,38,0.18)" : "rgba(220,38,38,0.1)",
    notSet: "#2563EB",
    notSetChip: isDark ? "rgba(37,99,235,0.2)" : "rgba(37,99,235,0.1)",
    na: isDark ? "#94A3B8" : "#64748B",
    naChip: isDark ? "rgba(148,163,184,0.16)" : "rgba(100,116,139,0.1)",
    source: {
      spark: {
        fg: isDark ? "#93C5FD" : "#2F6BB0",
        labelFg: isDark ? "#BFDBFE" : "#3B6EA5",
        bg: isDark ? "rgba(47,107,176,0.28)" : "#E3F0FF",
        border: isDark ? "rgba(147,197,253,0.35)" : "#B9D4F5",
        iconBg: "#3B82F6",
        wave: isDark
          ? "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(59,130,246,0.55), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(147,197,253,0.28), transparent 50%)"
          : "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(59,130,246,0.22), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(147,197,253,0.55), transparent 50%)",
      },
      manual: {
        fg: isDark ? "#FBBF24" : "#C2410C",
        labelFg: isDark ? "#FDE68A" : "#9A3412",
        bg: isDark ? "rgba(194,65,12,0.22)" : "#FFF4E5",
        border: isDark ? "rgba(251,191,36,0.35)" : "#F5D0A9",
        iconBg: "#F59E0B",
        wave: isDark
          ? "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(245,158,11,0.45), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(251,191,36,0.25), transparent 50%)"
          : "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(245,158,11,0.28), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(253,230,138,0.7), transparent 50%)",
      },
      third: {
        fg: isDark ? "#C4B5FD" : "#6D28D9",
        labelFg: isDark ? "#DDD6FE" : "#5B21B6",
        bg: isDark ? "rgba(109,40,217,0.24)" : "#F3EEFF",
        border: isDark ? "rgba(196,181,253,0.35)" : "#D8CFF5",
        iconBg: "#8B5CF6",
        wave: isDark
          ? "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(139,92,246,0.5), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(196,181,253,0.28), transparent 50%)"
          : "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(139,92,246,0.22), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(221,214,254,0.75), transparent 50%)",
      },
    },
    attention: {
      gaps: {
        fg: isDark ? "#FDBA74" : "#C2410C",
        bg: isDark ? "rgba(194,65,12,0.22)" : "#FFF1E6",
        border: isDark ? "rgba(251,146,60,0.4)" : "#FDBA74",
        iconBg: isDark ? "rgba(251,146,60,0.28)" : "#FFEDD5",
      },
      stale: {
        fg: isDark ? "#C4B5FD" : "#6D28D9",
        bg: isDark ? "rgba(109,40,217,0.22)" : "#F3EEFF",
        border: isDark ? "rgba(167,139,250,0.4)" : "#D8B4FE",
        iconBg: isDark ? "rgba(167,139,250,0.28)" : "#EDE9FE",
      },
      unset: {
        fg: isDark ? "#93C5FD" : "#1D4ED8",
        bg: isDark ? "rgba(37,99,235,0.22)" : "#E8F1FF",
        border: isDark ? "rgba(96,165,250,0.4)" : "#93C5FD",
        iconBg: isDark ? "rgba(96,165,250,0.28)" : "#DBEAFE",
      },
      overridden: {
        fg: isDark ? "#86EFAC" : "#15803D",
        bg: isDark ? "rgba(22,163,74,0.16)" : "#F0FDF4",
        border: isDark ? "rgba(22,163,74,0.35)" : "#BBF7D0",
        iconBg: isDark ? "rgba(22,163,74,0.28)" : "#DCFCE7",
      },
    },
  };
}

/**
 * SPARK Compliance Dashboard palette:
 * radial #55718B → #B4C3D1, chart accents #6366F1 / #FB923C,
 * pass/fail greens & reds from dashboard widgets.
 */
function sparkThemes(isDark: boolean): SummaryAccentTheme {
  const slate = isDark ? "#B4C3D1" : "#55718B";
  const slateSoft = isDark ? "#8B9EB0" : "#6B7D8F";
  const indigo = isDark ? "#A5B4FC" : "#6366F1";
  const orange = isDark ? "#FDBA74" : "#FB923C";
  const pass = isDark ? "#4ADE80" : "#059669";
  const fail = isDark ? "#F87171" : "#DC2626";

  return {
    scoreRing: slate,
    scoreTrack: isDark ? "rgba(255,255,255,0.1)" : "#eee",
    scoreShadow: isDark
      ? "none"
      : "drop-shadow(0 8px 16px rgba(85,113,139,0.22))",
    divider: isDark
      ? "linear-gradient(180deg, transparent, rgba(180,195,209,0.35), transparent)"
      : "linear-gradient(180deg, transparent, #B4C3D1, transparent)",
    accentBlue: slate,
    accentOrange: orange,
    accentIndigo: indigo,
    selectedBorder: isDark ? "rgba(180,195,209,0.65)" : "#55718B",
    selectedShadow: isDark
      ? "0 0 0 2px rgba(180,195,209,0.22)"
      : "0 0 0 2px rgba(85,113,139,0.18), 0 8px 18px rgba(85,113,139,0.12)",
    selectedSectionBg: isDark ? "rgba(180,195,209,0.12)" : "#EEF2F6",
    viewDetails: slate,
    compliant: pass,
    compliantChip: isDark ? "rgba(74,222,128,0.16)" : "rgba(5,150,105,0.1)",
    nonCompliant: fail,
    nonCompliantChip: isDark ? "rgba(248,113,113,0.16)" : "rgba(220,38,38,0.1)",
    notSet: slate,
    notSetChip: isDark ? "rgba(180,195,209,0.16)" : "rgba(85,113,139,0.1)",
    na: isDark ? "#8B9EB0" : "#6B7D8F",
    naChip: isDark ? "rgba(139,158,176,0.16)" : "rgba(107,125,143,0.1)",
    source: {
      spark: {
        fg: slate,
        labelFg: slateSoft,
        bg: isDark ? "rgba(180,195,209,0.12)" : "#F3F6F9",
        border: isDark ? "rgba(180,195,209,0.28)" : "#B4C3D1",
        iconBg: "#55718B",
        wave: isDark
          ? "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(180,195,209,0.35), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(85,113,139,0.35), transparent 50%)"
          : "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(85,113,139,0.18), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(180,195,209,0.65), transparent 50%)",
      },
      manual: {
        fg: orange,
        labelFg: isDark ? "#FDE68A" : "#C2410C",
        bg: isDark ? "rgba(251,146,60,0.14)" : "#FFF7ED",
        border: isDark ? "rgba(251,146,60,0.35)" : "#FDBA74",
        iconBg: "#FB923C",
        wave: isDark
          ? "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(251,146,60,0.4), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(253,186,116,0.22), transparent 50%)"
          : "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(251,146,60,0.22), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(253,230,138,0.55), transparent 50%)",
      },
      third: {
        fg: indigo,
        labelFg: isDark ? "#C7D2FE" : "#4F46E5",
        bg: isDark ? "rgba(99,102,241,0.16)" : "#EEF2FF",
        border: isDark ? "rgba(165,180,252,0.35)" : "#C7D2FE",
        iconBg: "#6366F1",
        wave: isDark
          ? "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(99,102,241,0.4), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(165,180,252,0.22), transparent 50%)"
          : "radial-gradient(ellipse 140% 100% at 10% 120%, rgba(99,102,241,0.16), transparent 55%), radial-gradient(ellipse 120% 80% at 95% 110%, rgba(199,210,254,0.7), transparent 50%)",
      },
    },
    attention: {
      gaps: {
        fg: orange,
        bg: isDark ? "rgba(251,146,60,0.14)" : "#FFF7ED",
        border: isDark ? "rgba(251,146,60,0.4)" : "#FDBA74",
        iconBg: isDark ? "rgba(251,146,60,0.28)" : "#FFEDD5",
      },
      stale: {
        fg: indigo,
        bg: isDark ? "rgba(99,102,241,0.16)" : "#EEF2FF",
        border: isDark ? "rgba(165,180,252,0.4)" : "#C7D2FE",
        iconBg: isDark ? "rgba(165,180,252,0.28)" : "#E0E7FF",
      },
      unset: {
        fg: slate,
        bg: isDark ? "rgba(180,195,209,0.12)" : "#F3F6F9",
        border: isDark ? "rgba(180,195,209,0.35)" : "#B4C3D1",
        iconBg: isDark ? "rgba(180,195,209,0.22)" : "#E8EEF3",
      },
      overridden: {
        fg: pass,
        bg: isDark ? "rgba(74,222,128,0.12)" : "#ECFDF5",
        border: isDark ? "rgba(74,222,128,0.35)" : "#A7F3D0",
        iconBg: isDark ? "rgba(74,222,128,0.22)" : "#D1FAE5",
      },
    },
  };
}

export function getSummaryAccentTheme(isDark: boolean): SummaryAccentTheme {
  return USE_SPARK_SUMMARY_THEME ? sparkThemes(isDark) : pastelThemes(isDark);
}
